import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getInitialState,
  updateComponent,
} from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

type CtaLinkOption = {
  label: string;
  value: string;
};

export const LINK_URL_SOURCE_TYPES = {
  CUSTOM_URL: 'custom-url',
  MARKETPLACE_OFFER: 'marketplace-offer',
  MODAL: 'modal',
};

export const useDynamicLink = () => {
  const dispatch = useDispatch();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { componentPath, marketplaceOffers } = useDynamicGrid({
    componentId: selectedComponentId,
  });
  const currentComponent = flatOutline?.[selectedComponentId];

  const [url, setUrl] = useState('');
  const [urlSource, setUrlSource] = useState('custom-url');
  const [marketplaceOfferId, setMarketplaceOfferId] = useState('');
  const [linkOptions, setLinkOptions] = useState<CtaLinkOption[]>([]);
  const [selectedLinkOption, setSelectedLinkOption] = useState('');
  const [authenticatedUrl, setAuthenticatedUrl] = useState('');
  const [captureCustomQueryParams, setCaptureCustomQueryParams] =
    useState(false);
  const [targetBlank, setTargetBlank] = useState(false);

  const handleUpdateComponent = useCallback(
    (field: string, value: string | boolean | number) => {
      set(currentComponent, `props.${componentPath}.${field}`, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, dispatch]
  );

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpdateComponent('url', e.target.value);
    setUrl(e.target.value);
  };

  const handleAuthenticatedUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAuthenticatedUrl(e.target.value);
    handleUpdateComponent('authenticatedUrl', e.target.value);
  };

  const handleTargetBlankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTargetBlank(e.target.checked);
    handleUpdateComponent('targetBlank', e.target.checked);
  };

  const handleUrlSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUrlSource(e.target.value);
    handleUpdateComponent('urlSource', e.target.value);

    // reset marketplace offer id when changing to custom url
    if (e.target.value === LINK_URL_SOURCE_TYPES.CUSTOM_URL) {
      setMarketplaceOfferId('');
      handleUpdateComponent('marketplaceOfferId', '');
    }

    // reset entity name when changing to marketplace offer
    if (e.target.value === LINK_URL_SOURCE_TYPES.MARKETPLACE_OFFER) {
      handleUpdateComponent('entityName', '');
    }
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    const selectedOffer = marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
    const linkUrl = get(selectedOffer, value, '');
    setUrl(linkUrl);
    setSelectedLinkOption(value);
    handleUpdateComponent('url', linkUrl);
    handleUpdateComponent('linkOption', value);
  };

  const handleCaptureCustomQueryParamsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCaptureCustomQueryParams(e.target.checked);
    handleUpdateComponent('captureCustomQueryParams', e.target.checked);
  };

  const urlSourceOptions = useCallback(() => {
    const options = [
      {
        label: 'Custom URL',
        value: LINK_URL_SOURCE_TYPES.CUSTOM_URL,
      },
      {
        label: 'Modal',
        value: LINK_URL_SOURCE_TYPES.MODAL,
      },
    ];

    if (!marketplaceOffers?.length) {
      return options;
    }

    options.push({
      label: 'Marketplace Offer',
      value: LINK_URL_SOURCE_TYPES.MARKETPLACE_OFFER,
    });

    return options;
  }, [marketplaceOffers]);

  const marketplaceOfferOptions = useCallback(() => {
    const options = marketplaceOffers?.map((offer: any) => ({
      label: offer.name,
      value: offer.id,
    }));
    options.unshift({
      label: 'Select a Marketplace Offer',
      value: '',
    });
    return options;
  }, [marketplaceOffers]);

  const handleMarketplaceOfferIdChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { value: selectedOfferId } = e.target;
      setMarketplaceOfferId(selectedOfferId);
      handleUpdateComponent('marketplaceOfferId', selectedOfferId);
    },
    [handleUpdateComponent]
  );

  useEffect(() => {
    const selectedOffer = marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );

    if (!selectedOffer) {
      return;
    }

    if (!linkOptions?.length) {
      setLinkOptions([
        {
          label: 'Select a Link',
          value: '',
        },
        {
          label: 'CTA Link',
          value: 'cta.link',
        },
        {
          label: 'Rates and Fees Link',
          value: 'termsAndFeesLink',
        },
      ]);
    }
  }, [linkOptions, marketplaceOfferId, marketplaceOffers, setLinkOptions]);

  useEffect(() => {
    setUrl(getInitialState(currentComponent, componentPath, 'url', ''));
    setUrlSource(
      getInitialState(
        currentComponent,
        componentPath,
        'urlSource',
        LINK_URL_SOURCE_TYPES.CUSTOM_URL
      )
    );
    setMarketplaceOfferId(
      getInitialState(currentComponent, componentPath, 'marketplaceOfferId', '')
    );
    setSelectedLinkOption(
      getInitialState(currentComponent, componentPath, 'linkOption', '')
    );
    setCaptureCustomQueryParams(
      getInitialState(
        currentComponent,
        componentPath,
        'captureCustomQueryParams',
        false
      )
    );
    setAuthenticatedUrl(
      getInitialState(currentComponent, componentPath, 'authenticatedUrl', '')
    );
    setTargetBlank(
      getInitialState(currentComponent, componentPath, 'targetBlank', false)
    );
  }, [
    currentComponent,
    componentPath,
    setUrl,
    setUrlSource,
    setMarketplaceOfferId,
  ]);

  return {
    currentComponent,
    componentPath,
    marketplaceOffers,
    ctaLinkOptions: linkOptions,
    selectedLinkOption,
    handleLinkChange,
    handleMarketplaceOfferIdChange,
    urlSourceOptions,
    url,
    urlSource,
    marketplaceOfferId,
    handleUrlChange,
    handleUrlSourceChange,
    marketplaceOfferOptions,
    setUrl,
    setUrlSource,
    setMarketplaceOfferId,
    setLinkOptions,
    captureCustomQueryParams,
    handleCaptureCustomQueryParamsChange,
    handleAuthenticatedUrlChange,
    authenticatedUrl,
    targetBlank,
    handleTargetBlankChange,
  };
};
